import React, { useState } from 'react';
import api from '../services/api';
import './PatientCheckIn.css';

function PatientCheckIn() {
  const [step, setStep] = useState('identify'); // identify, verify, confirm
  const [patientType, setPatientType] = useState(null); // new or existing
  const [formData, setFormData] = useState({
    // For existing patients
    phoneNumber: '',
    dateOfBirth: '',
    // For new patients
    firstName: '',
    lastName: '',
    email: '',
    address: '',
    insuranceProvider: '',
    insuranceId: '',
    emergencyContact: '',
    emergencyPhone: '',
  });
  const [patientData, setPatientData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePatientTypeSelection = (type) => {
    setPatientType(type);
    setStep(type === 'existing' ? 'verify' : 'register');
    setError('');
  };

  const verifyExistingPatient = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/patients/verify', {
        phoneNumber: formData.phoneNumber,
        dateOfBirth: formData.dateOfBirth
      });

      if (response.data.success) {
        setPatientData(response.data.data);

        // Show warning if in fallback mode
        if (response.data.warning) {
          alert('⚠️ Note: ' + response.data.warning);
        }

        setStep('confirm');
      } else {
        setError('Patient not found. Please check your information or register as a new patient.');
      }
    } catch (err) {
      // Handle error - extract string message from error object
      const errorData = err.response?.data?.error;
      const errorMessage = err.response?.data?.error?.message
        || (typeof errorData === 'object' && errorData?.message ? errorData.message : null)
        || (typeof errorData === 'string' ? errorData : null)
        || err.response?.data?.message
        || err.message
        || 'Failed to verify patient information';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const registerNewPatient = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/patients/register', formData);

      if (response.data.success) {
        setPatientData(response.data.data);

        // Show warning if in fallback mode
        if (response.data.warning) {
          alert('⚠️ Note: ' + response.data.warning);
        }

        setStep('confirm');
      }
    } catch (err) {
      // Handle error - extract string message from error object
      const errorData = err.response?.data?.error;
      const errorMessage = err.response?.data?.error?.message
        || (typeof errorData === 'object' && errorData?.message ? errorData.message : null)
        || (typeof errorData === 'string' ? errorData : null)
        || err.response?.data?.message
        || err.message
        || 'Failed to register patient';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const completeCheckIn = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/checkins', {
        patientId: patientData.id,
        checkInTime: new Date().toISOString()
      });

      let message = 'Check-in successful! Please have a seat and wait to be called.';

      // Show warning if in fallback mode
      if (response.data.warning) {
        message += '\n\n⚠️ Note: ' + response.data.warning;
      }

      alert(message);
      resetForm();
    } catch (err) {
      // Handle error - extract string message from error object
      const errorData = err.response?.data?.error;
      const errorMessage = err.response?.data?.error?.message
        || (typeof errorData === 'object' && errorData?.message ? errorData.message : null)
        || (typeof errorData === 'string' ? errorData : null)
        || err.response?.data?.message
        || err.message
        || 'Failed to complete check-in';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep('identify');
    setPatientType(null);
    setFormData({
      phoneNumber: '',
      dateOfBirth: '',
      firstName: '',
      lastName: '',
      email: '',
      address: '',
      insuranceProvider: '',
      insuranceId: '',
      emergencyContact: '',
      emergencyPhone: '',
    });
    setPatientData(null);
    setError('');
  };

  return (
    <div className="patient-checkin">
      <div className="checkin-header">
        <h2>Patient Check-In</h2>
        {step !== 'identify' && (
          <button className="back-btn" onClick={resetForm}>
            ← Start Over
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Step 1: Identify Patient Type */}
      {step === 'identify' && (
        <div className="patient-type-selection">
          <h3>Welcome! Are you a new or existing patient?</h3>
          <div className="type-buttons">
            <button
              className="type-btn existing"
              onClick={() => handlePatientTypeSelection('existing')}
            >
              <span className="icon">👤</span>
              <span className="label">Existing Patient</span>
              <span className="description">I have visited before</span>
            </button>
            <button
              className="type-btn new"
              onClick={() => handlePatientTypeSelection('new')}
            >
              <span className="icon">✨</span>
              <span className="label">New Patient</span>
              <span className="description">First time visit</span>
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Verify Existing Patient */}
      {step === 'verify' && (
        <div className="verification-form">
          <h3>Verify Your Information</h3>
          <form onSubmit={verifyExistingPatient}>
            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                placeholder="(555) 123-4567"
                required
              />
            </div>
            <div className="form-group">
              <label>Date of Birth</label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                required
              />
            </div>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify & Check In'}
            </button>
          </form>
        </div>
      )}

      {/* Step 2 (Alt): Register New Patient */}
      {step === 'register' && (
        <div className="registration-form">
          <h3>New Patient Registration</h3>
          <form onSubmit={registerNewPatient}>
            <div className="form-section">
              <h4>Personal Information</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Date of Birth *</label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="(555) 123-4567"
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="patient@example.com"
                />
              </div>
              <div className="form-group">
                <label>Address *</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  rows="2"
                  required
                />
              </div>
            </div>

            <div className="form-section">
              <h4>Insurance Information</h4>
              <div className="form-group">
                <label>Insurance Provider *</label>
                <input
                  type="text"
                  name="insuranceProvider"
                  value={formData.insuranceProvider}
                  onChange={handleInputChange}
                  placeholder="e.g., Blue Cross Blue Shield"
                  required
                />
              </div>
              <div className="form-group">
                <label>Insurance ID *</label>
                <input
                  type="text"
                  name="insuranceId"
                  value={formData.insuranceId}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-section">
              <h4>Emergency Contact</h4>
              <div className="form-group">
                <label>Contact Name *</label>
                <input
                  type="text"
                  name="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Contact Phone *</label>
                <input
                  type="tel"
                  name="emergencyPhone"
                  value={formData.emergencyPhone}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Registering...' : 'Register & Check In'}
            </button>
          </form>
        </div>
      )}

      {/* Step 3: Confirm Check-In */}
      {step === 'confirm' && patientData && (
        <div className="confirmation">
          <div className="success-icon">✓</div>
          <h3>Welcome{patientType === 'existing' ? ' Back' : ''}, {patientData.firstName}!</h3>
          <div className="patient-info">
            <div className="info-row">
              <span className="label">Name:</span>
              <span className="value">{patientData.firstName} {patientData.lastName}</span>
            </div>
            <div className="info-row">
              <span className="label">Date of Birth:</span>
              <span className="value">{patientData.dateOfBirth}</span>
            </div>
            <div className="info-row">
              <span className="label">Phone:</span>
              <span className="value">{patientData.phoneNumber}</span>
            </div>
            {patientData.insuranceProvider && (
              <div className="info-row">
                <span className="label">Insurance:</span>
                <span className="value">{patientData.insuranceProvider}</span>
              </div>
            )}
          </div>
          <p className="confirm-message">Please confirm this information is correct:</p>
          <div className="confirm-buttons">
            <button className="confirm-btn" onClick={completeCheckIn} disabled={loading}>
              {loading ? 'Checking In...' : 'Confirm & Complete Check-In'}
            </button>
            <button className="cancel-btn" onClick={resetForm}>
              Not Me / Start Over
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PatientCheckIn;
